import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import config from "../config";

/** Σελίδες επαναφοράς κωδικού: αίτημα (/forgot-password) και ορισμός (/reset-password/:token). */
export default function ResetPasswordPage({ mode = "reset" }) {
  const { t } = useTranslation();
  const { token } = useParams();
  const [state, setState] = useState(mode === "reset" ? "checking" : "form");
  const [info, setInfo] = useState(null);
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (mode !== "reset" || !token) return;
    axios.get(`${config.API_BASE_URL}/public/auth/reset/${token}`)
      .then(({ data }) => { setInfo(data); setState("form"); })
      .catch(() => setState("invalid"));
  }, [mode, token]);

  const sendForgot = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await axios.post(`${config.API_BASE_URL}/public/auth/forgot`, { email });
      setState("sent");
    } catch {
      setState("sent"); // ίδια απάντηση πάντα — καμία διαρροή
    } finally { setBusy(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setError(t("The two passwords do not match."));
    if (pw.length < 8) return setError(t("Password must be at least 8 characters."));
    setBusy(true); setError(null);
    try {
      await axios.post(`${config.API_BASE_URL}/public/auth/reset`, { token, password: pw });
      setState("done");
    } catch (err) {
      setError(err?.response?.data?.error === "token-invalid-or-expired"
        ? t("This link has expired or was already used.")
        : t("Something went wrong — please try again."));
    } finally { setBusy(false); }
  };

  const S = {
    wrap: { maxWidth: 400, margin: "60px auto", padding: "0 20px", fontFamily: "system-ui, sans-serif" },
    input: { width: "100%", padding: 12, fontSize: 15, borderRadius: 10, border: "1px solid #d1d5db",
             margin: "6px 0", boxSizing: "border-box" },
    btn: { width: "100%", padding: 13, fontSize: 15, fontWeight: 700, borderRadius: 10, border: "none",
           background: "#d97706", color: "#fff", cursor: "pointer", marginTop: 10 },
    hint: { color: "#6b7280", fontSize: 14 },
    err: { background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5",
           borderRadius: 10, padding: "10px 14px", fontSize: 14, margin: "10px 0" },
    ok: { background: "#ecfdf3", color: "#065f46", border: "1px solid #a7f3d0",
          borderRadius: 10, padding: "12px 14px", fontSize: 14, margin: "10px 0" },
  };

  if (state === "checking") return <div style={S.wrap}>…</div>;

  if (state === "invalid") return (
    <div style={S.wrap}>
      <h2>{t("Link not valid")}</h2>
      <div style={S.err}>{t("This link has expired or was already used.")}</div>
      <Link to="/forgot-password"><button style={S.btn}>{t("Request a new link")}</button></Link>
    </div>
  );

  if (state === "sent") return (
    <div style={S.wrap}>
      <h2>{t("Check your inbox")} 📬</h2>
      <div style={S.ok}>
        {t("If an account exists with that email, we sent a reset link. It is valid for 60 minutes.")}
      </div>
      <p style={S.hint}>{t("Don't forget to check your spam folder.")}</p>
      <Link to="/login"><button style={S.btn}>{t("Back to login")}</button></Link>
    </div>
  );

  if (state === "done") return (
    <div style={S.wrap}>
      <h2>{t("Password changed")} ✅</h2>
      <div style={S.ok}>{t("You can now log in with your new password.")}</div>
      <Link to={info?.company ? `/login/${info.company}` : "/login"}>
        <button style={S.btn}>{t("Go to login")}</button>
      </Link>
    </div>
  );

  // mode === "forgot"
  if (mode === "forgot") return (
    <div style={S.wrap}>
      <h2>{t("Forgot your password?")}</h2>
      <p style={S.hint}>{t("Enter your email and we'll send you a link to set a new password.")}</p>
      <form onSubmit={sendForgot}>
        <input style={S.input} type="email" required placeholder="Email"
          value={email} onChange={e => setEmail(e.target.value)} />
        {error && <div style={S.err}>{error}</div>}
        <button style={S.btn} type="submit" disabled={busy}>{t("Send reset link")}</button>
      </form>
      <p style={{ ...S.hint, textAlign: "center", marginTop: 14 }}>
        <Link to="/login">{t("Back to login")}</Link>
      </p>
    </div>
  );

  // mode === "reset", έγκυρο token
  return (
    <div style={S.wrap}>
      <h2>{t("Set a new password")}</h2>
      {info?.username && <p style={S.hint}>{t("Account")}: <b>{info.username}</b></p>}
      <form onSubmit={submitReset}>
        <input style={S.input} type="password" required minLength={8} autoFocus
          placeholder={t("New password (min. 8 characters)")}
          value={pw} onChange={e => setPw(e.target.value)} autoComplete="new-password" />
        <input style={S.input} type="password" required minLength={8}
          placeholder={t("Repeat password")}
          value={pw2} onChange={e => setPw2(e.target.value)} autoComplete="new-password" />
        {error && <div style={S.err}>{error}</div>}
        <button style={S.btn} type="submit" disabled={busy}>{t("Save new password")}</button>
      </form>
    </div>
  );
}
